import { useEffect, useRef, useState } from "react";
import useInView from "@/lib/useInView";

/**
 * AnimatedCounter — counts up from 0 to `to` when first scrolled into view.
 * Honors prefers-reduced-motion via useInView's reduce-shortcut (renders final).
 *
 * Props:
 *   - to: number target (required)
 *   - duration: ms (default 1600)
 *   - prefix, suffix: optional strings shown around the number
 *   - decimals: 0 (default) – number of decimal places
 *   - className: passthrough
 */
export default function AnimatedCounter({
  to,
  duration = 1600,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
  "data-testid": testId,
}) {
  const [ref, inView] = useInView({ threshold: 0.4 });
  const [val, setVal] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setVal(to * eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else setVal(to);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [inView, to, duration]);

  const display = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString();

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
